/**
 * @fileoverview AI spreadsheet → workflow import dialog (MOR-696, renamed MOR-716)
 *
 * Uploads a CSV/XLSX export from another task tool, runs the AI import action
 * to produce a reviewable draft workflow, persists it (create + replaceItems),
 * then opens the editor so the user can review and refine before it's used.
 */

import { useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { FileSpreadsheet, Loader2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { extractErrorMessage } from "@/utils/errors";

const ACCEPT = ".csv,.xlsx,.xls";

export function WorkflowImportDialog({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: Id<"organizations">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const convex = useConvex();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await convex.mutation(
        api.tasks.workflows.generateImportUploadUrl,
        {},
      );
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      const { storageId } = (await response.json()) as {
        storageId: Id<"_storage">;
      };

      const draft = await convex.action(
        api.tasks.workflowImport.importTemplateFromSpreadsheet,
        { orgId, storageId, fileName: file.name },
      );

      const templateId = await convex.mutation(api.tasks.workflows.create, {
        orgId,
        name: draft.name,
        description: draft.description ?? undefined,
      });
      await convex.mutation(api.tasks.workflows.replaceItems, {
        templateId,
        items: draft.items.map((it) => ({
          localKey: it.localKey,
          title: it.title,
          description: it.description ?? undefined,
          priority: it.priority ?? undefined,
          statusKey: it.statusKey ?? undefined,
          labelKeys: it.labelKeys.length > 0 ? it.labelKeys : undefined,
          assigneeUserId: it.assigneeUserId ?? undefined,
          assignorUserId: it.assignorUserId ?? undefined,
          parentLocalKey: it.parentLocalKey ?? undefined,
          blockedByKeys: it.blockedByKeys,
          dueOffsetDays: it.dueOffsetDays ?? undefined,
        })),
      });

      toast.success(
        `Imported ${draft.items.length} task${draft.items.length === 1 ? "" : "s"} — review and save.`,
      );
      onOpenChange(false);
      await navigate({
        to: "/dashboard/workflows/$workflowId",
        params: { workflowId: templateId },
      });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not import that spreadsheet."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            Import tasks from a spreadsheet
          </DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel export from another task tool. We'll use AI to
            turn it into a draft workflow — with subtasks, dependencies,
            statuses and assignees (matched to your firm's members) inferred —
            that you can review before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="flex w-full items-center gap-3 rounded-lg border border-dashed border-border px-4 py-5 text-left transition-colors hover:bg-muted/50 disabled:opacity-60"
          >
            {file ? (
              <FileSpreadsheet className="size-6 shrink-0 text-primary" />
            ) : (
              <Upload className="size-6 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-primary">
                {file ? file.name : "Choose a file"}
              </p>
              <p className="text-xs text-muted-foreground">
                {file
                  ? "Click to choose a different file"
                  : "CSV or Excel (.csv, .xlsx)"}
              </p>
            </div>
          </button>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleImport()} disabled={!file || busy}>
            {busy ? (
              <>
                <Loader2 className="mr-1.5 size-4 animate-spin" />
                Importing…
              </>
            ) : (
              <>
                <Sparkles className="mr-1.5 size-4" />
                Import with AI
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
